import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Star, ShieldCheck, ArrowRight } from 'lucide-react';
import { PageHero, FadeIn, SectionLabel } from '../components/Shared';
import WaveMeshBg from '../components/bg/WaveMeshBg';

const PLANS = [
    {
        slug: 'starter',
        name: 'Starter',
        badge: 'Essential',
        price: '£499',
        features: [
            '5 pages (Home, About, Services, Contact, FAQ)',
            'Responsive mobile-first design',
            'Contact form with email notifications',
            'Basic SEO setup (meta tags, sitemap)',
            'Google Analytics integration',
            '1 round of revisions',
            'Delivery: 7 business days',
            '30 days free support after launch',
        ],
    },
    {
        slug: 'professional',
        name: 'Professional',
        badge: 'Best Value',
        price: '£899',
        popular: true,
        features: [
            'Up to 8 pages',
            'Premium responsive design',
            'Blog / News section',
            'Advanced SEO (schema markup, speed optimisation)',
            'Google Analytics 4 + Search Console setup',
            'Social media integration',
            '3 rounds of revisions',
            'Delivery: 10 business days',
            '60 days free support after launch',
        ],
    },
    {
        slug: 'premium',
        name: 'Premium',
        badge: 'Full Package',
        price: '£1,499',
        features: [
            '8+ custom pages',
            'Bespoke design from scratch',
            'AI chatbot integration',
            'E-commerce ready (WooCommerce or Shopify)',
            'Full SEO suite + local SEO',
            'Blog + newsletter setup',
            'Unlimited revisions',
            'Delivery: 14 business days',
            '90 days free support after launch',
            'Priority support channel',
        ],
    },
];

const FAQS = [
    { q: 'Can I upgrade my package later?', a: 'Absolutely. You can add pages or features at any time.' },
    { q: 'Do you offer payment plans?', a: 'Yes, we offer 50/50 split — half upfront, half on delivery.' },
    { q: 'What if I need more than 8 pages?', a: 'Our Premium package includes 8+ pages. For larger projects, contact us for a custom quote.' },
    { q: 'Is the security plan mandatory?', a: 'No, but we strongly recommend it. The first month is free with any web package.' },
];

const Pricing = () => {
    return (
        <main className="bg-white text-[#1A1A2E] pb-24" data-testid="pricing-page">
            <PageHero
                label="Pricing"
                title="Simple, transparent pricing."
                subtitle="No hidden fees. No surprises. Choose the package that fits your business."
                testId="pricing-hero"
                bg={<WaveMeshBg />}
            />

            {/* ============== TIERS ============== */}
            <section className="py-12 bg-white" data-testid="pricing-tiers">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {PLANS.map((p, i) => (
                            <FadeIn key={p.slug} delay={i * 0.1}>
                                <div
                                    className={`relative h-full p-8 rounded-3xl flex flex-col transition-all duration-500 ${
                                        p.popular
                                            ? 'bg-[#1A1A2E] text-white border border-[#1A1A2E] shadow-card-hover'
                                            : 'bg-white border border-[#E8E8EC] hover:shadow-card-hover hover:-translate-y-1'
                                    }`}
                                    data-testid={`pricing-card-${p.slug}`}
                                >
                                    {p.popular && (
                                        <div className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-[#7B61FF] text-white text-[11px] font-semibold px-3 py-1.5 rounded-full">
                                            <Star className="w-3 h-3" fill="currentColor" /> Most popular
                                        </div>
                                    )}

                                    <div className="mb-5">
                                        <span
                                            className={`text-[11px] font-semibold tracking-[0.1em] uppercase ${
                                                p.popular ? 'text-[#00E5CC]' : 'text-[#7B61FF]'
                                            }`}
                                        >
                                            {p.badge}
                                        </span>
                                    </div>
                                    <h3
                                        className={`font-display text-2xl font-semibold tracking-tight ${
                                            p.popular ? 'text-white' : 'text-[#1A1A2E]'
                                        }`}
                                    >
                                        {p.name}
                                    </h3>
                                    <div className="mt-5 flex items-baseline gap-2">
                                        <span
                                            className={`font-display text-5xl font-semibold tracking-tight ${
                                                p.popular ? 'text-white' : 'text-[#1A1A2E]'
                                            }`}
                                        >
                                            {p.price}
                                        </span>
                                        <span
                                            className={`text-sm ${
                                                p.popular ? 'text-white/60' : 'text-[#6B6B7A]'
                                            }`}
                                        >
                                            one-time
                                        </span>
                                    </div>

                                    <ul className="mt-7 space-y-3 flex-1">
                                        {p.features.map((f, j) => (
                                            <li
                                                key={j}
                                                className={`flex items-start gap-3 text-[14px] ${
                                                    p.popular ? 'text-white/80' : 'text-[#4A4A5A]'
                                                }`}
                                            >
                                                <span
                                                    className={`inline-flex items-center justify-center w-5 h-5 rounded-full shrink-0 mt-0.5 ${
                                                        p.popular ? 'bg-[#00E5CC]/20' : 'bg-[#E6FBF6]'
                                                    }`}
                                                >
                                                    <Check
                                                        className="w-3 h-3"
                                                        style={{ color: p.popular ? '#00E5CC' : '#00B89E' }}
                                                        strokeWidth={3}
                                                    />
                                                </span>
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <Link
                                        to={`/contact?plan=${p.slug}`}
                                        className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-[14px] transition-all duration-300 ${
                                            p.popular
                                                ? 'bg-[#7B61FF] text-white hover:bg-[#937EFF]'
                                                : 'bg-[#1A1A2E] text-white hover:bg-[#7B61FF]'
                                        }`}
                                        data-testid={`pricing-cta-${p.slug}`}
                                    >
                                        Choose {p.name} <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============== SECURITY ADD-ON ============== */}
            <section className="py-24 bg-white" data-testid="security-addon-section">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    <FadeIn>
                        <SectionLabel>Add-on</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A2E] leading-[1.15]">
                            Add monthly security protection.
                        </h2>
                    </FadeIn>

                    <FadeIn delay={0.1}>
                        <div
                            className="mt-12 p-10 bg-gradient-to-br from-[#F7F7F8] to-[#E6FBF6] border border-[#E8E8EC] rounded-3xl relative overflow-hidden"
                            data-testid="security-plan-card"
                        >
                            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                                <div className="md:col-span-7">
                                    <div className="flex items-center gap-3 mb-4">
                                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white border border-[#E8E8EC]">
                                            <ShieldCheck className="w-5 h-5 text-[#00B89E]" />
                                        </span>
                                        <span className="text-[12px] font-semibold tracking-[0.1em] uppercase text-[#00B89E]">
                                            AI Security Plan
                                        </span>
                                    </div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="font-display text-5xl font-semibold text-[#1A1A2E] tracking-tight">£49</span>
                                        <span className="text-[#6B6B7A]">/month</span>
                                    </div>
                                    <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {[
                                            'Monthly AI code audit & vulnerability scan',
                                            'SSL certificate & security headers monitoring',
                                            'Plugin & dependency updates',
                                            'Monthly security report with risk score',
                                            'GDPR compliance check',
                                            'Priority security incident response',
                                            'Cancel anytime — no long-term contract',
                                        ].map((f, j) => (
                                            <li key={j} className="flex items-start gap-2.5 text-[14px] text-[#4A4A5A]">
                                                <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#00B89E]" strokeWidth={3} />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="md:col-span-5 flex md:justify-end">
                                    <Link
                                        to="/contact?plan=security"
                                        className="inline-flex items-center gap-2 bg-[#1A1A2E] text-white font-semibold text-[14px] px-7 py-3.5 rounded-full hover:bg-[#00B89E] transition-all duration-300"
                                        data-testid="pricing-cta-security"
                                    >
                                        Add Security Plan <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </FadeIn>

                    <p className="mt-6 text-[14px] text-[#6B6B7A] text-center">
                        All web development packages include a free first month of security monitoring.
                    </p>
                </div>
            </section>

            {/* ============== MINI FAQ ============== */}
            <section className="py-24 bg-[#F7F7F8]" data-testid="pricing-faq">
                <div className="max-w-4xl mx-auto px-6 lg:px-8">
                    <FadeIn>
                        <SectionLabel>FAQ</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A2E] leading-[1.15]">
                            Quick answers.
                        </h2>
                    </FadeIn>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {FAQS.map((f, i) => (
                            <FadeIn key={i} delay={(i % 2) * 0.1}>
                                <div
                                    className="bg-white border border-[#E8E8EC] rounded-2xl p-6 h-full"
                                    data-testid={`pricing-faq-${i + 1}`}
                                >
                                    <h3 className="font-display text-[17px] font-semibold tracking-tight text-[#1A1A2E]">
                                        {f.q}
                                    </h3>
                                    <p className="mt-2 text-[15px] text-[#4A4A5A] leading-relaxed">
                                        {f.a}
                                    </p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Pricing;
